import { useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import {
  VideoCamera,
  Phone,
  Microphone,
  MicrophoneSlash,
  Camera as CameraIcon,
  CameraSlash,
  Flag,
  Clock,
  User,
  Calendar,
  ArrowRight,
  CheckCircle
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VideoCall = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [match, setMatch] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inCall, setInCall] = useState(false);

  const fetchMatchDetails = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/time-slots/${matchId}`);
      setMatch(response.data.match);
      setSlots(response.data.slots);
    } catch (error) {
      toast.error('Failed to load match details');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [matchId, navigate]);

  useEffect(() => {
    fetchMatchDetails();
  }, [fetchMatchDetails]);

  const handleSelectSlot = async () => {
    if (!selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/time-slots/${matchId}/select`, {
        slot: selectedSlot
      });
      toast.success(response.data.message);
      if (response.data.status === 'scheduled') {
        setMatch(prev => ({ 
          ...prev, 
          status: 'scheduled', 
          jitsi_link: response.data.jitsi_link,
          scheduled_time: selectedSlot
        }));
      } else {
        fetchMatchDetails();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to select time slot';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const joinJitsiCall = () => {
    if (match?.jitsi_link) {
      window.open(match.jitsi_link, '_blank');
      setInCall(true);
    }
  };

  const endCall = () => {
    setInCall(false);
    navigate(`/feedback/${matchId}`);
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </PageLayout>
    );
  }

  // If scheduling - show time slot selection
  if (match?.status === 'scheduling') {
    return (
      <PageLayout>
        <div className="py-12 md:py-16">
          <div className="max-w-2xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <Calendar weight="light" className="w-16 h-16 text-primary mx-auto mb-6" />
              <h1 className="font-serif text-3xl md:text-4xl mb-4">
                Choose Your Call Time
              </h1>
              <p className="text-muted-foreground">
                Select a time that works best for you. Once your match confirms the same slot, 
                the video call will be scheduled.
              </p>
            </div>

            <div className="paper-card rounded-sm p-8">
              <div className="space-y-4 mb-8">
                {slots.map((slot) => (
                  <button
                    key={slot.id}
                    onClick={() => setSelectedSlot(slot.slot)}
                    className={`w-full p-4 rounded-sm border-2 text-left transition-all ${
                      selectedSlot === slot.slot
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid={`time-slot-${slot.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-primary" />
                        <span className="font-medium">
                          {new Date(slot.slot).toLocaleString()}
                        </span>
                      </div>
                      {selectedSlot === slot.slot && (
                        <CheckCircle weight="fill" className="w-5 h-5 text-primary" />
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <Button
                onClick={handleSelectSlot}
                disabled={!selectedSlot || submitting}
                className="btn-primary w-full py-6"
                data-testid="confirm-time-slot-btn"
              >
                {submitting ? 'Confirming...' : 'Confirm Time Slot'}
              </Button>

              {match.user_a_selection || match.user_b_selection ? (
                <p className="text-center text-muted-foreground text-sm mt-4">
                  {match.user_a_selection === selectedSlot || match.user_b_selection === selectedSlot
                    ? "You've selected this slot. Waiting for your match to confirm."
                    : "Your match has selected a different slot. Choose the same slot to confirm."}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // If scheduled - show call interface
  if (match?.status === 'scheduled') {
    return (
      <PageLayout>
        <div className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <div className="paper-card rounded-sm overflow-hidden">
              {/* Call Header */}
              <div className="bg-stone-900 text-white p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <User weight="light" className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-serif text-xl">Video Call</h2>
                      <p className="text-stone-400 text-sm">
                        Scheduled: {new Date(match.scheduled_time).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {inCall && (
                    <span className="flex items-center gap-2 text-emerald-400">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      In Call
                    </span>
                  )}
                </div>
              </div>

              {/* Call Content */}
              <div className="p-8">
                {!inCall ? (
                  <div className="text-center py-12">
                    <VideoCamera weight="thin" className="w-24 h-24 text-primary/30 mx-auto mb-6" />
                    <h3 className="font-serif text-2xl mb-4">Ready to Connect?</h3>
                    <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                      Click the button below to join your video call. The call will open 
                      in a new tab using Jitsi Meet.
                    </p>
                    <Button
                      onClick={joinJitsiCall}
                      className="btn-primary text-lg px-10 py-6"
                      data-testid="join-call-btn"
                    >
                      <VideoCamera className="w-5 h-5 mr-2" />
                      Join Video Call
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                      <VideoCamera weight="fill" className="w-12 h-12 text-emerald-600" />
                    </div>
                    <h3 className="font-serif text-2xl mb-4">Call In Progress</h3>
                    <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                      Your video call is open in another tab. When you're done, 
                      click the button below to end the call and provide feedback.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button
                        onClick={() => window.open(match.jitsi_link, '_blank')}
                        variant="outline"
                        className="btn-secondary"
                      >
                        Rejoin Call
                      </Button>
                      <Button
                        onClick={endCall}
                        className="btn-primary"
                        data-testid="end-call-btn"
                      >
                        End Call & Give Feedback
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Safety Notice */}
              <div className="bg-amber-50 border-t border-amber-200 p-4">
                <div className="flex items-start gap-3">
                  <Flag weight="fill" className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <span className="font-medium text-amber-800">Safety First: </span>
                    <span className="text-amber-700">
                      If you feel uncomfortable at any point, you can end the call immediately. 
                      Use the feedback form to report any inappropriate behavior.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Fallback - redirect to dashboard
  return (
    <PageLayout>
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-2xl mb-4">Match Not Ready</h2>
          <p className="text-muted-foreground mb-6">
            This match is not yet ready for video calls.
          </p>
          <Link to="/dashboard">
            <Button className="btn-primary">Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    </PageLayout>
  );
};

export default VideoCall;
